import imageCompression from 'browser-image-compression';

export interface ImageVariant {
  size: 'original' | 'large' | 'medium' | 'small' | 'thumbnail';
  url: string;
  width: number;
  quality: number;
}

export interface CompressionResult {
  variants: ImageVariant[];
  primary: string;
}

const compressionConfigs = {
  original: {
    maxWidthOrHeight: 5000,
    useWebWorker: true,
    quality: 0.98,
    preserveExif: true
  },
  large: {
    maxWidthOrHeight: 2560,
    useWebWorker: true,
    quality: 0.95
  },
  medium: {
    maxWidthOrHeight: 1920,
    useWebWorker: true,
    quality: 0.93
  },
  small: {
    maxWidthOrHeight: 1200,
    useWebWorker: true,
    quality: 0.92
  },
  thumbnail: {
    maxWidthOrHeight: 600,
    useWebWorker: true,
    quality: 0.90
  }
};

export async function compressImageWithVariants(
  file: File,
  generateVariants: boolean = true
): Promise<{ files: { size: string; file: File }[]; fileType: string }> {
  const fileType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';

  if (!generateVariants) {
    const compressed = await imageCompression(file, {
      ...compressionConfigs.large,
      fileType
    });
    return {
      files: [{ size: 'large', file: compressed }],
      fileType
    };
  }

  const variants: { size: string; file: File }[] = [];

  try {
    const original = await imageCompression(file, {
      ...compressionConfigs.original,
      fileType
    });
    variants.push({ size: 'original', file: original });
  } catch (error) {
    console.warn('Failed to create original variant:', error);
  }

  try {
    const large = await imageCompression(file, {
      ...compressionConfigs.large,
      fileType
    });
    variants.push({ size: 'large', file: large });
  } catch (error) {
    console.error('Failed to create large variant:', error);
  }

  try {
    const medium = await imageCompression(file, {
      ...compressionConfigs.medium,
      fileType
    });
    variants.push({ size: 'medium', file: medium });
  } catch (error) {
    console.warn('Failed to create medium variant:', error);
  }

  try {
    const small = await imageCompression(file, {
      ...compressionConfigs.small,
      fileType
    });
    variants.push({ size: 'small', file: small });
  } catch (error) {
    console.warn('Failed to create small variant:', error);
  }

  try {
    const thumbnail = await imageCompression(file, {
      ...compressionConfigs.thumbnail,
      fileType
    });
    variants.push({ size: 'thumbnail', file: thumbnail });
  } catch (error) {
    console.warn('Failed to create thumbnail variant:', error);
  }

  if (variants.length === 0) {
    throw new Error('Failed to create any image variants');
  }

  return { files: variants, fileType };
}

export function getImageUrl(baseUrl: string, size: 'original' | 'large' | 'medium' | 'small' | 'thumbnail' = 'large'): string {
  if (size === 'large' || !baseUrl.includes('/gallery/') || baseUrl.includes(`-${size}.`)) {
    return baseUrl;
  }

  const ext = baseUrl.substring(baseUrl.lastIndexOf('.'));
  const baseWithoutExt = baseUrl.substring(0, baseUrl.lastIndexOf('.'));

  return `${baseWithoutExt}-${size}${ext}`;
}

export function getOptimalImageSize(containerWidth: number): 'thumbnail' | 'small' | 'medium' | 'large' | 'original' {
  const dpr = window.devicePixelRatio || 1;
  const targetWidth = containerWidth * dpr;

  if (targetWidth <= 400) return 'thumbnail';
  if (targetWidth <= 800) return 'small';
  if (targetWidth <= 1200) return 'medium';
  if (targetWidth <= 1920) return 'large';
  return 'original';
}
